import { useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import '../chartSetup';
import { PAL, buildDayRange, filterDailyMap, fmtN, fmtUSD, fmtUSDc } from '../shared';
import { ND_COLORS } from '../theme';
import type { WindowState } from '../shared';
import { buildDashboardWindowView } from '../dashboardExport';
import type { DashboardWindowView } from '../dashboardExport';
import { useCurrency } from '../currencyContext';
import type { ClientSnapshot, DashboardView } from '@/types/fleetAnalytics';

interface CompareTabProps {
  snapshots: ClientSnapshot[];
  window: WindowState;
}

const MAX_SELECTED = 6;

const fmtCredits = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString(undefined, { maximumFractionDigits: 1 });

interface MetricRow {
  label: string;
  value: (v: DashboardWindowView, snap: ClientSnapshot) => string;
  title?: string;
}

const METRICS: MetricRow[] = [
  {
    label: 'Total spend (inferred)',
    value: (v) => fmtUSD(v.spend.inferred_total),
    title: 'Quota-sharing-aware: Claude CE lines swapped for token-priced chat cost',
  },
  { label: 'Raw AWS CE', value: (v) => fmtUSD(v.spend.raw_ce_total) },
  { label: 'Chat/Agent LLM spend', value: (v) => fmtUSD(v.spend.chat_llm) },
  {
    label: 'Credits used (window)',
    value: (v) => (v.credits?.available ? fmtCredits(v.credits.used_in_window) : '—'),
  },
  {
    label: 'Credits used (this month)',
    value: (v) => (v.credits?.available ? fmtCredits(v.credits.current_month_used) : '—'),
    title: 'NZ billing month',
  },
  {
    label: 'Credit balance',
    value: (v) => (v.credits?.available ? fmtCredits(v.credits.available_balance) : '—'),
    title: 'Live top-up balance — negative = unbilled overflow (invoice signal)',
  },
  { label: 'Conversations', value: (v) => fmtN(v.volume.conversations) },
  { label: 'User messages', value: (v) => fmtN(v.volume.user_messages) },
  { label: 'Cost / conversation', value: (v) => (v.volume.conversations ? fmtUSD(v.spend.avg_cost_per_conv) : '—') },
  { label: 'Cost / user message', value: (v) => (v.volume.user_messages ? fmtUSDc(v.spend.cost_per_user_msg) : '—') },
  { label: 'Scheduled runs', value: (v) => fmtN(v.spend.scheduled_runs_count) },
  { label: 'Active users', value: (v) => fmtN(v.operations.active_users) },
  { label: 'Provisioned users', value: (_v, s) => fmtN(s.users?.provisioned_users || 0) },
  { label: 'Agents', value: (_v, s) => fmtN((s.agents?.agents?.count || 0) + (s.agents?.user_agents?.count || 0)) },
  { label: 'Active schedules', value: (v) => fmtN(v.operations.active_schedules || 0) },
];

/**
 * Compare tab — pick up to six stacks and see their windowed KPIs side by
 * side, plus a daily chat-cost overlay. All numbers respect the window picker
 * and reuse the exact same math as the Overview/export layers
 * (buildDashboardWindowView), so a figure here always matches the same stack's
 * own Overview page.
 */
export function CompareTab({ snapshots, window }: CompareTabProps) {
  useCurrency();
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<string[]>(() =>
    [...snapshots]
      .sort((a, b) => (b.chat?.totals?.cost || 0) - (a.chat?.totals?.cost || 0))
      .slice(0, 2)
      .map((s) => s.client)
  );

  const byName = useMemo(() => {
    const out: Record<string, ClientSnapshot> = {};
    for (const s of snapshots) out[s.client] = s;
    return out;
  }, [snapshots]);

  const pickable = useMemo(() => {
    const names = snapshots.map((s) => s.client).sort((a, b) => a.localeCompare(b));
    const f = filter.trim().toLowerCase();
    return f ? names.filter((n) => n.toLowerCase().includes(f)) : names;
  }, [snapshots, filter]);

  const toggle = (client: string) => {
    setSelected((prev) => {
      if (prev.includes(client)) return prev.filter((c) => c !== client);
      if (prev.length >= MAX_SELECTED) return prev;
      return [...prev, client];
    });
  };

  const views = useMemo(
    () =>
      selected
        .map((c) => byName[c])
        .filter(Boolean)
        .map((snap) => ({
          snap,
          view: buildDashboardWindowView(snap as unknown as DashboardView, window, snapshots),
        })),
    [selected, byName, window, snapshots]
  );

  const days = useMemo(() => buildDayRange(window.startDate, window.endDate), [window]);
  const chartData = useMemo(
    () => ({
      labels: days,
      datasets: views.map(({ snap }, i) => {
        const daily = filterDailyMap(snap.chat?.daily_cost, window);
        return {
          label: snap.client,
          data: days.map((d) => daily[d] || 0),
          borderColor: PAL[i % PAL.length],
          backgroundColor: PAL[i % PAL.length],
          pointRadius: 0,
          borderWidth: 2,
          tension: 0.25,
        };
      }),
    }),
    [views, days, window]
  );

  return (
    <>
      <div className="nd-row-1">
        <div className="nd-card">
          <div className="nd-card-header">
            <div>
              <div className="nd-card-title">Compare stacks</div>
              <div className="nd-card-subtitle">
                Pick up to {MAX_SELECTED} stacks — KPIs below use the selected window and the same inferred-cost math as
                Overview
              </div>
            </div>
            <input
              className="form-control form-control-sm"
              style={{ width: 200 }}
              placeholder="Filter stacks…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
          <div className="nd-compare-picker">
            {pickable.map((name) => {
              const active = selected.includes(name);
              const disabled = !active && selected.length >= MAX_SELECTED;
              return (
                <button
                  key={name}
                  type="button"
                  className={`nd-window-btn ${active ? 'nd-active' : ''}`}
                  style={disabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                  onClick={() => !disabled && toggle(name)}
                  title={disabled ? `Deselect a stack first (max ${MAX_SELECTED})` : name}
                >
                  {name}
                </button>
              );
            })}
            {pickable.length === 0 && <span className="text-muted small">No stacks match the filter</span>}
          </div>
        </div>
      </div>

      {views.length > 0 && (
        <>
          <div className="nd-row-1">
            <div className="nd-card">
              <div className="nd-card-header">
                <div>
                  <div className="nd-card-title">Side by side · {window.label}</div>
                  <div className="nd-card-subtitle">— means no data (e.g. no credit ledger on that stack)</div>
                </div>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="nd-table">
                  <thead>
                    <tr>
                      <th>Metric</th>
                      {views.map(({ snap }) => (
                        <th key={snap.client} className="nd-num">
                          {snap.client}
                          {snap.client_config?.dev_instance && (
                            <span className="nd-pill nd-warn-pill" style={{ fontSize: 9, marginLeft: 6 }}>
                              DEV
                            </span>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {METRICS.map((m) => (
                      <tr key={m.label}>
                        <td title={m.title}>{m.label}</td>
                        {views.map(({ snap, view }) => (
                          <td key={snap.client} className="nd-num" title={m.title}>
                            {m.value(view, snap)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="nd-row-1">
            <div className="nd-card">
              <div className="nd-card-header">
                <div>
                  <div className="nd-card-title">Daily chat/agent LLM cost</div>
                  <div className="nd-card-subtitle">
                    Token-priced trace cost per stack (truly per-stack, unlike account-wide CE)
                  </div>
                </div>
              </div>
              <div className="nd-chart-wrap" style={{ height: 320 }}>
                <Line
                  data={chartData}
                  options={{
                    maintainAspectRatio: false,
                    interaction: { mode: 'index', intersect: false },
                    plugins: { legend: { position: 'bottom', labels: { color: ND_COLORS.text } } },
                    scales: {
                      x: { ticks: { color: ND_COLORS.textDim, maxTicksLimit: 12 } },
                      y: { ticks: { color: ND_COLORS.textDim } },
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
